<template>
    <!-- The recap is a split-pane desktop surface and tabbed mobile flow, followed by rating/scheduling screens. -->
    <BaseDialog v-model:open="isWrapUpDialogOpen" :title="t('chat.wrapUp.dialogTitle')" layout="custom">
        <BaseButton theme="neutral" icon-only class="absolute top-4 right-4 z-10" :aria-label="t('chat.wrapUp.closeLabel')" @click="$emit('close')">
            <i class="bi bi-x-lg" aria-hidden="true"></i>
        </BaseButton>
        <div v-if="currentStep === WRAP_UP_STEPS.SUMMARY">
            <div class="flex flex-col items-center gap-2 pt-6 md:hidden">
                <div class="h-12 w-12 md:h-16 md:w-16">
                    <CoachingModeMarble :is-loading="false" />
                </div>
                <h1 class="display-serif">{{ t("chat.recap.title") }}</h1>
            </div>
            <!-- unmount-on-hide is set to false so that chat summary state persists through tab navigation while the dialog is open -->
            <BaseTabs
                :unmount-on-hide="false"
                class="block p-6 md:hidden"
                :tab-ids="tabIds"
                :labels="{ summary: t('chat.wrapUp.tabs.summary'), notes: t('chat.wrapUp.tabs.notes') }">
                <template #content-summary>
                    <div class="w-full py-6"><ChatSummary :hide-email-button="props.hideEmailButton" :show-continue-button="true" @continue="handleContinue" /></div>
                </template>
                <template #content-notes>
                    <div class="py-6"><ChatRecap :insights="insights" :user-name="userName" :profile-inferences="profileInferences" /></div>
                </template>
            </BaseTabs>
            <div class="hidden md:grid md:w-3xl md:grid-cols-2">
                <div class="grid place-items-center bg-surface-sunken">
                    <ChatRecap :insights="insights" :profile-inferences="profileInferences" class="p-10" :user-name="userName" />
                </div>
                <ChatSummary :hide-email-button="props.hideEmailButton" :show-continue-button="true" @continue="handleContinue" />
            </div>
        </div>
        <div v-else-if="currentStep === WRAP_UP_STEPS.FEEDBACK" class="flex w-full flex-col gap-5 px-10 py-10 md:w-162.5">
            <h1 :id="ratingLabelId" class="title-subsection font-normal">{{ t("chat.wrapUp.feedback.question") }}</h1>

            <!-- Rating Buttons -->
            <BaseToggleGroup
                v-model="selectedRating"
                :options="ratingOptions"
                :required="false"
                appearance="chips"
                :aria-labelledby="ratingLabelId"
                class="w-full justify-between" />

            <!-- Optional Details - only show after rating selected -->
            <div v-if="selectedRating" class="flex flex-col gap-2">
                <BaseTextarea
                    v-model:input="feedbackComment"
                    disable-autosize
                    :aria-label="t('chat.wrapUp.feedback.detailsPrompt')"
                    class="feedback-comment"
                    rows="3"
                    :placeholder="t('chat.wrapUp.feedback.detailsPrompt')" />
            </div>

            <!-- Outreach Consent Checkbox -->
            <BaseCheckbox v-if="showOutreachConsent" v-model="outreachConsent" checkbox-id="outreach-consent" shape="square">
                <span class="body-small text-ink-muted">{{ t("chat.wrapUp.feedback.outreachConsent") }}</span>
            </BaseCheckbox>

            <!-- Submit Button -->
            <div class="flex justify-end gap-2">
                <BaseButton theme="secondary" @click="handleFeedbackContinue">{{ t("chat.wrapUp.skip") }}</BaseButton>
                <BaseButton data-testid="submit-feedback" theme="primary" :disabled="!isValidRating || isSubmittingFeedback" @click="submitFeedback">
                    <span v-if="isSubmittingFeedback">{{ t("chat.wrapUp.feedback.submitting") }}</span>
                    <span v-else-if="props.sessionFeedback">{{ t("chat.wrapUp.feedback.update") }}</span>
                    <span v-else>{{ t("chat.wrapUp.feedback.share") }}</span>
                </BaseButton>
            </div>
        </div>
        <template v-else>
            <div class="flex min-h-96 flex-col items-center justify-center gap-6 p-6 md:max-w-125 md:p-10">
                <div class="h-16 w-16">
                    <CoachingModeMarble :is-loading="false" />
                </div>
                <h1 class="title-subsection">{{ wrapUpTitle }}</h1>
                <p class="body text-center text-ink-muted">{{ wrapUpDescription }}</p>
                <template v-if="currentStep === WRAP_UP_STEPS.SCHEDULE">
                    <ScheduleFollowUp :date="date" :recurring="recurring" @date-update="handleDateUpdated" @recurring-update="handleRecurringUpdated" />
                    <div class="flex flex-col gap-2">
                        <BaseButton theme="primary" :disabled="isLoadingEventDescription" @click="scheduleFollowup">{{ t("chat.wrapUp.schedule.submit") }}</BaseButton>
                        <BaseButton theme="tertiary" @click="handleSkip">{{ t("chat.wrapUp.skip") }}</BaseButton>
                    </div>
                </template>
                <div v-else-if="currentStep === WRAP_UP_STEPS.COMPLETE" class="flex flex-col gap-2">
                    <BaseButton v-if="props.isPerformanceReviewSession" theme="primary" @click="handleBackToOverview"> {{ t("chat.wrapUp.complete.backToOverview") }} </BaseButton>
                    <BaseButton v-else theme="primary" @click="openUrl('/')"> {{ t("chat.wrapUp.complete.goHome") }} </BaseButton>
                </div>
            </div>
        </template>
    </BaseDialog>
</template>

<script setup lang="ts">
import { nextWorkday, roundToNearest15 } from "/js/dateUtils";
import { useFetch } from "~vue/api";
import type { GlobalProperties, OrganizationDetails } from "~vue/appTypes";
import { ACTION, pluckAction, pluckMultipleActions } from "~vue/chatActions";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseTabs from "~vue/components/BaseTabs.vue";
import BaseToggleGroup from "~vue/components/BaseToggleGroup.vue";
import BaseCheckbox from "~vue/components/form/BaseCheckbox.vue";
import BaseTextarea from "~vue/components/form/BaseTextarea.vue";
import CoachingModeMarble from "~vue/components/navigation/CoachingModeMarble.vue";
import ScheduleFollowUp from "~vue/ScheduleFollowUp.vue";
import type { ChatMessage } from "~vue/types/chat";
import type { UserProfileData } from "~vue/types/profile";
import { openUrl } from "~vue/utils";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { formatDate } from "~vue/utils/formatDate";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { convertSnakeCaseToDisplay } from "~vue/utils/stringUtils";
import { DateTime } from "luxon";
import type { Ref } from "vue";
import { computed, inject, ref, useId, watch } from "vue";
import { useI18n } from "vue-i18n";

import ChatRecap from "./ChatRecap.vue";
import ChatSummary from "./ChatSummary.vue";

type RatingValue = "not_at_all" | "somewhat" | "mostly" | "completely" | "exceeded";

interface SessionFeedback {
    rating?: RatingValue;
    comment?: string;
}

interface Props {
    messages: ChatMessage[];
    userFirstName?: string;
    userLastName?: string;
    hideEmailButton?: boolean;
    enableFollowUp?: boolean;
    sessionFeedback?: SessionFeedback | null;
    isPerformanceReviewSession?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    userFirstName: "",
    userLastName: "",
    hideEmailButton: false,
    enableFollowUp: true,
    sessionFeedback: null,
    isPerformanceReviewSession: false,
});

defineEmits<{
    close: [];
}>();

const { t } = useI18n();

const coachingSessionId = inject<string>("coachingSessionId")!;
const coachingSessionUuid = inject<string>("coachingSessionUuid")!;
const { $sendEvent } = inject<GlobalProperties>("globalProperties", {} as GlobalProperties);
const userDetails = inject<{ value: { prefers_weekly_check_in: boolean } }>("user_details")!;
const organizationDetails = inject<Ref<OrganizationDetails | null>>("organization_details");

const showOutreachConsent = computed(() => organizationDetails?.value?.show_outreach_consent !== false);

const WRAP_UP_STEPS = {
    SUMMARY: 0,
    FEEDBACK: 1,
    SCHEDULE: 2,
    COMPLETE: 3,
} as const;

const currentStep = ref<number>(WRAP_UP_STEPS.SUMMARY);
const ratingLabelId = useId();

const tabIds = ["summary", "notes"];

const insights = computed(() => pluckMultipleActions(props.messages, [ACTION.INTERNAL_THINKING, ACTION.VALUES_INSIGHT]));
const profileInferences = computed(() => {
    const profileInferenceActions = pluckMultipleActions(props.messages, [ACTION.INFERRED_PROFILE_ANSWER]);
    return profileInferenceActions
        .filter((action) => {
            const a = action;
            return !!a.line?.action_params;
        })
        .map<UserProfileData>((item) => {
            const {
                line: { action_params },
            } = item;
            return {
                id: +action_params.user_profile_data_id,
                profile_question_key: convertSnakeCaseToDisplay(action_params.key),
                value: action_params.inferred_answer,
            };
        });
});

const followup = computed(() => pluckAction(props.messages, ACTION.FOLLOW_UP)?.line);
const handleContinue = () => {
    // Always go to feedback step after summary
    currentStep.value = WRAP_UP_STEPS.FEEDBACK;
};

const handleFeedbackContinue = () => {
    if (!props.enableFollowUp) {
        currentStep.value = WRAP_UP_STEPS.COMPLETE;
        return;
    }

    if (followup.value) {
        // @ts-expect-error - going to bring in typing for specific actions (follow up) in smaller pieces
        if (!followup.value.action_params.event_at_confirmed && !followup.value.action_state.submitted) {
            currentStep.value = WRAP_UP_STEPS.SCHEDULE;
            return;
        }
        currentStep.value = WRAP_UP_STEPS.COMPLETE;
        return;
    }
    currentStep.value = WRAP_UP_STEPS.SCHEDULE;
};

const userName = computed(() => {
    if (props.userFirstName && props.userLastName) {
        return `${props.userFirstName} ${props.userLastName}`;
    }
    return props.userFirstName ?? props.userLastName;
});

const followupIsoDate = computed(() => followup.value?.action_params.event_at_confirmed ?? followup.value?.action_params.event_at_guessed ?? DateTime.now().plus({ days: 3 }));
const date = ref<Date>(nextWorkday(roundToNearest15(followupIsoDate.value)).toJSDate());
const recurring = ref<boolean>(userDetails.value.prefers_weekly_check_in);
const isLoadingEventDescription = ref<boolean>(false);
const followUpDescription = ref<string>("");

const handleDateUpdated = (newDate: Date) => {
    date.value = newDate;
};
const handleRecurringUpdated = (newRecurring: boolean) => {
    recurring.value = newRecurring;
};

// Feedback state
const ratingOptions = computed<Array<{ key: RatingValue; label: string }>>(() => [
    { key: "not_at_all", label: t("chat.wrapUp.feedback.ratings.notAtAll") },
    { key: "somewhat", label: t("chat.wrapUp.feedback.ratings.somewhat") },
    { key: "mostly", label: t("chat.wrapUp.feedback.ratings.mostly") },
    { key: "completely", label: t("chat.wrapUp.feedback.ratings.completely") },
    { key: "exceeded", label: t("chat.wrapUp.feedback.ratings.exceeded") },
]);

const validRatingValues = new Set<RatingValue>(["not_at_all", "somewhat", "mostly", "completely", "exceeded"]);
const selectedRating = ref<RatingValue | null>(props.sessionFeedback?.rating || null);
const feedbackComment = ref<string>(props.sessionFeedback?.comment || "");
const outreachConsent = ref<boolean>(false);
const hasLoggedFeedbackView = ref<boolean>(false);

// Fetch user settings to get outreach consent value
const { data: userSettingsData } = useFetch("/api/accounts/user/settings", { immediate: showOutreachConsent.value }).get().json();

watch(
    userSettingsData,
    (data) => {
        if (data?.settings) {
            const outreachSetting = data.settings.find((s: { key: string; value: boolean | null }) => s.key === "outreach_consent");
            if (outreachSetting?.value !== null && outreachSetting?.value !== undefined) {
                outreachConsent.value = outreachSetting.value;
            }
        }
    },
    { immediate: true },
);

// Update outreach consent via user settings endpoint
const outreachConsentPayload = ref<{ key: string; value: boolean } | null>(null);
const { execute: executeOutreachUpdate } = useFetch("/api/accounts/user/settings", { immediate: false }).post(outreachConsentPayload).json();

const updateOutreachConsent = async (value: boolean) => {
    outreachConsentPayload.value = { key: "outreach_consent", value };
    await executeOutreachUpdate();
};

// Feedback submission
const feedbackPayload = ref<{ coaching_session_id: string; feedback_data: { rating: RatingValue }; comment: string | null } | null>(null);
const { error: submitError, isFetching: isSubmittingFeedback, execute: executeSubmit } = useFetch("/api/chat/feedback/session/", { immediate: false }).post(feedbackPayload).json();

const ratingToNumber: Record<RatingValue, number> = {
    not_at_all: 1,
    somewhat: 2,
    mostly: 3,
    completely: 4,
    exceeded: 5,
};

const isValidRating = computed(() => selectedRating.value !== null && validRatingValues.has(selectedRating.value));

const submitFeedback = async () => {
    if (!isValidRating.value || !selectedRating.value) {
        return;
    }

    try {
        feedbackPayload.value = {
            coaching_session_id: coachingSessionId,
            feedback_data: { rating: selectedRating.value },
            comment: feedbackComment.value || null,
        };

        // Update outreach consent via user settings endpoint
        if (showOutreachConsent.value) {
            await updateOutreachConsent(outreachConsent.value);
        }

        await executeSubmit();

        if (submitError.value) {
            throw new Error(submitError.value.message || "Failed to submit feedback");
        }

        logUserInteraction(
            "convo_level_feedback",
            {
                ranking: ratingToNumber[selectedRating.value],
                ...(showOutreachConsent.value ? { outreach_consent: outreachConsent.value } : {}),
                session_id: coachingSessionUuid,
            },
            coachingSessionUuid,
        );

        // After feedback, continue to schedule or complete
        handleFeedbackContinue();
    } catch (e) {
        logError(e instanceof Error ? e : new Error(String(e)));
    }
};

const handleBackToOverview = () => {
    logUserInteraction(AmplitudeEventNames.PERFORMANCE_REVIEW_ENTRY_CLICKED, { entry_source: "back_to_overview" }, coachingSessionUuid);
    openUrl("/performance_management/");
};

const isWrapUpDialogOpen = ref<boolean>(false);
watch(
    isWrapUpDialogOpen,
    async () => {
        logUserInteraction("session_summary_viewed", {}, coachingSessionUuid);
        try {
            const response = await $sendEvent("generate_exit_follow_up_copy");
            // FIXME: some type narrowing for unknown type for now, needs to be generically typed
            if (response && typeof response === "object" && "payload" in response && typeof response.payload === "string") {
                followUpDescription.value = response.payload;
            }
        } finally {
            isLoadingEventDescription.value = false;
        }
    },
    { once: true },
);

// Log when feedback step is viewed
watch(currentStep, (newStep: number) => {
    if (newStep === WRAP_UP_STEPS.FEEDBACK && !hasLoggedFeedbackView.value) {
        logUserInteraction("session_feedback_viewed", {}, coachingSessionUuid);
        hasLoggedFeedbackView.value = true;
    }
});

const scheduledFollowup = ref<boolean>(!!followup.value?.action_params.event_at_confirmed);

const scheduleFollowup = async () => {
    currentStep.value = WRAP_UP_STEPS.COMPLETE;
    try {
        await $sendEvent("schedule_follow_up", {
            event_description: followUpDescription.value,
            event_at_confirmed: DateTime.fromJSDate(date.value).toISO(),
            event_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            recurring: recurring.value,
            type: ACTION.FOLLOW_UP,
            id: 0,
        });
        scheduledFollowup.value = true;
        logUserInteraction(AmplitudeEventNames.FOLLOWUP_SCHEDULED, { source: "session_summary" }, coachingSessionUuid);
    } catch (e) {
        logError(e);
    }
};

const handleSkip = () => {
    currentStep.value = WRAP_UP_STEPS.COMPLETE;
};

const wrapUpTitle = computed(() => {
    if (currentStep.value === WRAP_UP_STEPS.SCHEDULE) {
        return props.userFirstName ? t("chat.wrapUp.schedule.titleWithName", { name: props.userFirstName }) : t("chat.wrapUp.schedule.title");
    }
    if (scheduledFollowup.value) {
        const luxonDate = DateTime.fromJSDate(date.value);
        return t("chat.wrapUp.complete.scheduledTitle", { relativeDate: formatDate(luxonDate, "relative"), time: formatDate(luxonDate, "time") });
    }
    if (props.isPerformanceReviewSession) {
        return t("chat.wrapUp.complete.performanceReviewTitle");
    }
    return t("chat.wrapUp.complete.defaultTitle");
});
const wrapUpDescription = computed(() => {
    if (currentStep.value === WRAP_UP_STEPS.SCHEDULE) {
        return t("chat.wrapUp.schedule.description");
    }
    if (scheduledFollowup.value) {
        const luxonDate = DateTime.fromJSDate(date.value);
        const values = { date: formatDate(luxonDate, "month-day"), time: formatDate(luxonDate, "time") };
        return recurring.value ? t("chat.wrapUp.complete.scheduledDescriptionRecurring", values) : t("chat.wrapUp.complete.scheduledDescriptionOneTime", values);
    }
    if (props.isPerformanceReviewSession) {
        return t("chat.wrapUp.complete.performanceReviewDescription");
    }
    return t("chat.wrapUp.complete.defaultDescription");
});
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .feedback-comment {
        @apply w-full resize-none rounded-2xl border-2 border-control-border bg-control-bg text-control-text-strong p-4 transition-colors focus-field;
    }
}
</style>
